/**
 * USER CACHE UTILITY
 *
 * Caches Jira user details by accountId to avoid repeated API calls.
 * Uses localStorage for persistent caching across page refreshes.
 * Used when displaying user information in comments and attachments.
 */

import { getJiraUserById, getJiraUsersBulk, isJiraConnect } from './jiraContext'

/**
 * Cache duration: 2 weeks (in milliseconds)
 * User details rarely change, so we cache for an extended period
 */
const CACHE_DURATION_MS = 14 * 24 * 60 * 60 * 1000

/**
 * localStorage key for the user cache
 */
const STORAGE_KEY = 'jira_user_cache'

/**
 * Maximum number of users to store in cache
 * Prevents localStorage bloat in large organizations
 */
const MAX_CACHE_SIZE = 500

/**
 * LOAD CACHE FROM LOCALSTORAGE
 *
 * Reads the user cache from localStorage and converts it to a Map.
 * Returns empty Map if storage is empty or parsing fails.
 *
 * @returns {Map} User cache as a Map
 */
function loadCacheFromStorage() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (stored) {
      const parsed = JSON.parse(stored)
      return new Map(Object.entries(parsed))
    }
  } catch (error) {
    console.warn('Failed to load user cache from localStorage:', error)
  }
  return new Map()
}

/**
 * SAVE CACHE TO LOCALSTORAGE
 *
 * Saves the user cache to localStorage.
 * Prunes expired entries and limits size before saving.
 * Keeps newest entries when cache exceeds MAX_CACHE_SIZE.
 *
 * @param {Map} cache - User cache Map to save
 */
function saveCacheToStorage(cache) {
  try {
    const now = Date.now()
    const entries = []

    // Filter out expired entries
    for (const [accountId, data] of cache.entries()) {
      if (now - data.fetchedAt < CACHE_DURATION_MS) {
        entries.push([accountId, data])
      }
    }

    // Sort by fetchedAt (newest first) and limit size
    entries.sort((a, b) => b[1].fetchedAt - a[1].fetchedAt)
    const limitedEntries = entries.slice(0, MAX_CACHE_SIZE)

    // Convert to object for JSON storage
    const toStore = Object.fromEntries(limitedEntries)
    localStorage.setItem(STORAGE_KEY, JSON.stringify(toStore))
  } catch (error) {
    console.warn('Failed to save user cache to localStorage:', error)
  }
}

/**
 * In-memory cache for user details (loaded from localStorage on init)
 * Key: accountId
 * Value: { displayName, avatarUrl, fetchedAt }
 */
let userCache = loadCacheFromStorage()

/**
 * Track in-flight requests to avoid duplicate API calls
 * Key: accountId
 * Value: Promise<user>
 */
const pendingRequests = new Map()

/**
 * GET USER DETAILS
 *
 * Fetches user details from Jira API with caching.
 * If user is in cache and not expired, returns cached data.
 * If multiple requests are made for the same user simultaneously,
 * they share the same API call.
 *
 * @param {string} accountId - Jira account ID
 * @returns {Promise<Object|null>} User object { displayName, avatarUrl } or null
 */
export async function getUserDetails(accountId) {
  if (!accountId) {
    return null
  }

  // Check cache first
  const cached = userCache.get(accountId)
  if (cached) {
    const now = Date.now()
    // Return cached data if not expired
    if (now - cached.fetchedAt < CACHE_DURATION_MS) {
      return {
        displayName: cached.displayName,
        avatarUrl: cached.avatarUrl
      }
    }
    // Cache expired, remove it
    userCache.delete(accountId)
  }

  // Check if there's already a pending request for this user
  if (pendingRequests.has(accountId)) {
    return pendingRequests.get(accountId)
  }

  // Create new request
  const fetchPromise = (async () => {
    try {
      const user = await getJiraUserById(accountId)
      if (user) {
        // Store in cache
        userCache.set(accountId, {
          displayName: user.displayName,
          avatarUrl: user.avatarUrl,
          fetchedAt: Date.now()
        })
        // Persist to localStorage
        saveCacheToStorage(userCache)
        return {
          displayName: user.displayName,
          avatarUrl: user.avatarUrl
        }
      }
      return null
    } catch (error) {
      console.error('Failed to fetch user details:', error)
      return null
    } finally {
      // Remove from pending requests
      pendingRequests.delete(accountId)
    }
  })()

  // Track this request
  pendingRequests.set(accountId, fetchPromise)

  return fetchPromise
}

/**
 * GET USER DETAILS FROM CACHE ONLY
 *
 * Returns cached user details without making an API call.
 * Useful for synchronous rendering when you want to show
 * cached data immediately while fetching fresh data.
 *
 * @param {string} accountId - Jira account ID
 * @returns {Object|null} Cached user object or null if not cached
 */
export function getCachedUserDetails(accountId) {
  if (!accountId) return null

  const cached = userCache.get(accountId)
  if (cached) {
    const now = Date.now()
    if (now - cached.fetchedAt < CACHE_DURATION_MS) {
      return {
        displayName: cached.displayName,
        avatarUrl: cached.avatarUrl
      }
    }
  }
  return null
}

/**
 * PREFETCH USERS
 *
 * Fetches multiple user details using Jira's bulk API and caches them.
 * Uses a single bulk API call (batched in groups of 35) instead of
 * individual API calls per user for much better performance.
 * Falls back to individual fetching if bulk API fails.
 *
 * @param {string[]} accountIds - Array of Jira account IDs
 * @returns {Promise<void>}
 */
export async function prefetchUsers(accountIds) {
  if (!accountIds || accountIds.length === 0) return

  // Filter out already cached and null/undefined account IDs
  const idsToFetch = accountIds.filter(id => {
    if (!id) return false
    const cached = userCache.get(id)
    if (cached) {
      const now = Date.now()
      return now - cached.fetchedAt >= CACHE_DURATION_MS
    }
    return true
  })

  // Nothing to fetch if all are cached
  if (idsToFetch.length === 0) return

  try {
    // Use bulk API for efficient fetching (1-2 calls instead of N calls)
    const userMap = await getJiraUsersBulk(idsToFetch)

    // Populate cache with results from bulk fetch
    const now = Date.now()
    Object.entries(userMap).forEach(([accountId, user]) => {
      userCache.set(accountId, {
        displayName: user.displayName,
        avatarUrl: user.avatarUrl,
        fetchedAt: now
      })
    })

    // Persist to localStorage after bulk update
    saveCacheToStorage(userCache)

    // Check if any IDs were not returned by bulk API (might be deleted users, etc.)
    // Fall back to individual fetch for those (they'll likely return null but cache that)
    const fetchedIds = new Set(Object.keys(userMap))
    const missingIds = idsToFetch.filter(id => !fetchedIds.has(id))
    if (missingIds.length > 0) {
      // Fetch missing users individually in parallel
      await Promise.all(missingIds.map(id => getUserDetails(id)))
    }
  } catch (error) {
    // Bulk API failed - fall back to individual fetching
    console.warn('Bulk user fetch failed, falling back to individual fetches:', error)
    await Promise.all(idsToFetch.map(id => getUserDetails(id)))
  }
}

/**
 * CLEAR USER CACHE
 *
 * Clears all cached user data from memory and localStorage.
 * Useful for testing or when user data needs to be refreshed.
 */
export function clearUserCache() {
  userCache.clear()
  pendingRequests.clear()
  // Also clear localStorage
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch (error) {
    console.warn('Failed to clear user cache from localStorage:', error)
  }
}

/**
 * GET DISPLAY NAME OR FALLBACK
 *
 * Helper function to get display name with a fallback.
 * Returns the cached display name or a fallback while loading.
 *
 * @param {string} accountId - Jira account ID
 * @param {string} fallback - Fallback text if user not found (default: 'Unknown User')
 * @returns {string} Display name or fallback
 */
export function getDisplayNameSync(accountId, fallback = 'Unknown User') {
  const cached = getCachedUserDetails(accountId)
  return cached?.displayName || fallback
}

/**
 * CHECK IF RUNNING IN JIRA
 *
 * Re-export isJiraConnect for convenience.
 */
export { isJiraConnect }
